// Polymarket Bridge 支持的链/代币/最低额静态参考表。
// 来源：https://docs.polymarket.com/trading/bridge/supported-assets
// 1 个 evm 地址服务全部 EVM 链；充值资产自动兑换成 Polygon 上的 USDC.e 抵押。
import type { BridgeDepositAddresses } from './types';

export type BridgeAddrType = 'evm' | 'svm' | 'btc' | 'tron';

export interface BridgeChain {
  id: string; // 稳定 key
  label: string; // 展示名
  addrType: BridgeAddrType;
  minUsd: number; // 最低充值额（美元）
  tokens: string[]; // 示例代币
  iconUrl?: string; // 链图标（官网 favicon）
}

/** 链官网 favicon（与项目内其他链/币图标取法一致） */
function chainFavicon(domain: string) {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=256`;
}

// 下拉顺序 = 热度排序。
export const BRIDGE_CHAINS: BridgeChain[] = [
  {
    id: 'ethereum',
    label: 'Ethereum',
    addrType: 'evm',
    minUsd: 7,
    tokens: ['ETH', 'USDC', 'USDT', 'WBTC', 'DAI', 'LINK', 'UNI', 'AAVE'],
    iconUrl: chainFavicon('ethereum.org'),
  },
  {
    id: 'polygon',
    label: 'Polygon',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['USDC', 'POL', 'USDT', 'DAI', 'WETH', 'SAND'],
    iconUrl: chainFavicon('polygon.technology'),
  },
  {
    id: 'arbitrum',
    label: 'Arbitrum',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['ETH', 'ARB', 'USDC', 'USDT', 'DAI', 'WBTC', 'USDe'],
    iconUrl: chainFavicon('arbitrum.io'),
  },
  {
    id: 'base',
    label: 'Base',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['ETH', 'USDC', 'USDT', 'DAI', 'cbBTC', 'AERO', 'USDS'],
    iconUrl: chainFavicon('base.org'),
  },
  {
    id: 'optimism',
    label: 'Optimism',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['ETH', 'OP', 'USDC', 'USDT', 'DAI', 'USDe'],
    iconUrl: chainFavicon('optimism.io'),
  },
  {
    id: 'bnb',
    label: 'BNB Smart Chain',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['BNB', 'USDC', 'USDT', 'DAI', 'ETH', 'BTCB', 'BUSD'],
    iconUrl: chainFavicon('bnbchain.org'),
  },
  {
    id: 'solana',
    label: 'Solana',
    addrType: 'svm',
    minUsd: 2,
    tokens: ['SOL', 'USDC', 'USDT', 'USDe', 'TRUMP'],
    iconUrl: chainFavicon('solana.com'),
  },
  {
    id: 'bitcoin',
    label: 'Bitcoin',
    addrType: 'btc',
    minUsd: 9,
    tokens: ['BTC'],
    iconUrl: chainFavicon('bitcoin.org'),
  },
  {
    id: 'tron',
    label: 'Tron',
    addrType: 'tron',
    minUsd: 9,
    tokens: ['USDT'],
    iconUrl: chainFavicon('trondao.org'),
  },
  {
    id: 'hyperevm',
    label: 'HyperEVM',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['HYPE', 'USDC', 'USDe', 'stHYPE', 'UBTC', 'UETH'],
    iconUrl: chainFavicon('hyperliquid.xyz'),
  },
  {
    id: 'abstract',
    label: 'Abstract',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['ETH', 'USDC', 'USDT'],
    iconUrl: chainFavicon('abs.xyz'),
  },
  {
    id: 'monad',
    label: 'Monad',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['MON', 'USDC', 'USDT'],
    iconUrl: chainFavicon('monad.xyz'),
  },
  {
    id: 'ethereal',
    label: 'Ethereal',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['USDe', 'WUSDe'],
    iconUrl: chainFavicon('ethereal.trade'),
  },
  {
    id: 'katana',
    label: 'Katana',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['AUSD'],
    iconUrl: chainFavicon('katana.network'),
  },
  {
    id: 'lighter',
    label: 'Lighter',
    addrType: 'evm',
    minUsd: 2,
    tokens: ['USDC'],
    iconUrl: chainFavicon('lighter.xyz'),
  },
];

// 代币符号 → 项目官网域名（取 favicon 作图标）；包装/衍生币归到底层项目。
const TOKEN_ICON_DOMAINS: Record<string, string> = {
  ETH: 'ethereum.org',
  WETH: 'ethereum.org',
  UETH: 'ethereum.org',
  USDC: 'usdc.com',
  USDT: 'tether.to',
  BTC: 'bitcoin.org',
  WBTC: 'bitcoin.org',
  cbBTC: 'bitcoin.org',
  BTCB: 'bitcoin.org',
  UBTC: 'bitcoin.org',
  DAI: 'makerdao.com',
  LINK: 'chain.link',
  UNI: 'uniswap.org',
  AAVE: 'aave.com',
  POL: 'polygon.technology',
  SAND: 'sandbox.game',
  ARB: 'arbitrum.io',
  AERO: 'aerodrome.finance',
  USDS: 'sky.money',
  OP: 'optimism.io',
  BNB: 'bnbchain.org',
  BUSD: 'binance.com',
  SOL: 'solana.com',
  USDe: 'ethena.fi',
  WUSDe: 'ethena.fi',
  TRUMP: 'gettrumpmemes.com',
  HYPE: 'hyperliquid.xyz',
  stHYPE: 'hyperliquid.xyz',
  MON: 'monad.xyz',
  AUSD: 'agora.finance',
};

/** 代币图标 URL；未收录的符号返回 undefined（UI 回落首字母占位） */
export function getTokenIconUrl(symbol: string): string | undefined {
  const domain = TOKEN_ICON_DOMAINS[symbol];
  return domain ? chainFavicon(domain) : undefined;
}

export const DEFAULT_CHAIN_ID = 'polygon';

export function getBridgeChain(id: string): BridgeChain | undefined {
  return BRIDGE_CHAINS.find((c) => c.id === id);
}

// 给定链 id，从账户的 4 类桥地址中解析出该链应使用的唯一地址。
// 任一前置缺失（未知链 / 地址对象为空 / 该类型地址缺失）→ null，由 UI 兜底占位。
export function resolveBridgeAddress(
  addresses: BridgeDepositAddresses | null | undefined,
  chainId: string,
): { chain: BridgeChain; address: string } | null {
  const chain = getBridgeChain(chainId);
  if (!chain || !addresses) return null;
  const address = addresses[chain.addrType];
  if (!address) return null;
  return { chain, address };
}
